import { supabase } from './supabase';
import { isExcludedAnalyticsEvent } from './analyticsBotFilter';
import {
  getMonthDateRange,
  getOnlineBookingVolumeRange,
  type DateRangeInput,
  type OnlineBookingVolumePeriod,
} from './onlineBookingVolume';

export type WebsiteVisitsPeriod = OnlineBookingVolumePeriod;

export type WebsiteVisitsMonthYtdRow = {
  monthKey: string;
  label: string;
  visits: number;
  unique: number;
  conversionRate?: number;
  /** Counts for each conversion type that has events assigned. */
  conversionTypes: ConversionTypeTotal[];
  isCurrentMonth: boolean;
};

export type ConversionCriterionType = 'route' | 'click' | 'url_contains' | 'event';

export type ConversionTypeOption = {
  id: string;
  label: string;
};

/** Built-in labels. Custom types the business adds are stored beside these. */
export const SYSTEM_CONVERSION_TYPES: ConversionTypeOption[] = [
  { id: 'phone_call', label: 'Phone call' },
  { id: 'form_submission', label: 'Form submission' },
];

export type ConversionCriterion = {
  type: ConversionCriterionType;
  value: string;
  /** Which report label this event rolls up into. Empty until the user picks one. */
  conversionTypeId?: string;
};

export type WebsiteVisitsConversionConfig = {
  enabled: boolean;
  criteria: ConversionCriterion[];
  customTypes: ConversionTypeOption[];
};

export type ConversionTypeTotal = {
  id: string;
  label: string;
  count: number;
};

export function conversionTypeOptions(customTypes: ConversionTypeOption[]): ConversionTypeOption[] {
  const seen = new Set(SYSTEM_CONVERSION_TYPES.map((type) => type.id));
  const custom = customTypes.filter((type) => type.id && type.label.trim() && !seen.has(type.id));
  return [...SYSTEM_CONVERSION_TYPES, ...custom];
}

/** Caption under a type total: "1 form submission", "4 phone calls". */
export function conversionTypeCaption(label: string, count: number): string {
  const lower = label.trim().toLowerCase();
  if (count === 1 || lower.endsWith('s')) return lower;
  return `${lower}s`;
}

export type ConversionRouteOption = { route: string; sessions: number };
export type ConversionClickOption = {
  identifier: string;
  label: string;
  sessions: number;
};

export type WebsiteVisitsChartDataset = {
  label: string;
  data: number[];
  borderColor: string;
  backgroundColor: string;
  tension: number;
  fill: boolean;
  pointRadius: number;
  pointHoverRadius: number;
  pointBackgroundColor: string;
  pointBorderColor: string;
  pointBorderWidth: number;
  borderDash?: number[];
  yAxisID?: string;
  actualDates: string[];
};

export type WebsiteVisitsChartData = {
  labels: string[];
  datasets: WebsiteVisitsChartDataset[];
};

export type WebsiteVisitsGraphData = {
  value: string;
  uniqueValue: string;
  label: string;
  chartData: WebsiteVisitsChartData;
  uniqueChartData: WebsiteVisitsChartData;
  conversionRate?: string;
  /** Counts for conversion types that have at least one event assigned. */
  conversionTypes?: ConversionTypeTotal[];
};

export function emptyConversionConfig(): WebsiteVisitsConversionConfig {
  return { enabled: false, criteria: [], customTypes: [] };
}

function emptyChart(label: string, color: string): WebsiteVisitsChartData {
  return {
    labels: [],
    datasets: [
      {
        label,
        data: [],
        borderColor: color,
        backgroundColor: color.replace('rgb', 'rgba').replace(')', ', 0.1)'),
        tension: 0.4,
        fill: true,
        pointRadius: 4,
        pointHoverRadius: 6,
        pointBackgroundColor: color,
        pointBorderColor: 'white',
        pointBorderWidth: 2,
        actualDates: [],
      },
    ],
  };
}

export function emptyWebsiteVisitsGraphData(): WebsiteVisitsGraphData {
  return {
    value: '0',
    uniqueValue: '0',
    label: 'Website Visits',
    chartData: emptyChart('Daily Visits', 'rgb(59, 130, 246)'),
    uniqueChartData: emptyChart('Unique Visitors', 'rgb(147, 51, 234)'),
    conversionRate: undefined,
    conversionTypes: [],
  };
}

function periodUtcRange(period: WebsiteVisitsPeriod): { startIso: string; endIso: string } {
  const { start, end } = getOnlineBookingVolumeRange(period);
  const startDateUTC = new Date(Date.UTC(start.getFullYear(), start.getMonth(), start.getDate(), 0, 0, 0));
  const endDateUTC = new Date(Date.UTC(end.getFullYear(), end.getMonth(), end.getDate(), 23, 59, 59));
  return { startIso: startDateUTC.toISOString(), endIso: endDateUTC.toISOString() };
}

/** Load Control Centre conversion settings (business-level, same RPC math as KPI). */
export async function loadWebsiteVisitsConversionConfig(
  businessId: string,
): Promise<WebsiteVisitsConversionConfig> {
  try {
    const { data, error } = await supabase
      .from('business_analytics_config')
      .select('config')
      .eq('business_id', businessId)
      .maybeSingle();
    if (error) throw error;
    const cfg = data?.config?.conversionConfig as WebsiteVisitsConversionConfig | undefined;
    if (!cfg || typeof cfg !== 'object') return emptyConversionConfig();
    const criteria = Array.isArray(cfg.criteria)
      ? cfg.criteria.flatMap((c): ConversionCriterion[] => {
          if (
            !c ||
            (c.type !== 'route' &&
              c.type !== 'click' &&
              c.type !== 'url_contains' &&
              c.type !== 'event') ||
            typeof c.value !== 'string'
          ) {
            return [];
          }
          const conversionTypeId =
            typeof c.conversionTypeId === 'string' && c.conversionTypeId.trim()
              ? c.conversionTypeId
              : undefined;
          return [{ type: c.type, value: c.value, conversionTypeId }];
        })
      : [];
    const customTypes = Array.isArray(cfg.customTypes)
      ? cfg.customTypes.flatMap((type): ConversionTypeOption[] => {
          if (!type || typeof type.id !== 'string' || typeof type.label !== 'string') return [];
          const label = type.label.trim();
          if (!type.id.trim() || !label) return [];
          return [{ id: type.id, label }];
        })
      : [];
    return {
      enabled: !!cfg.enabled,
      criteria,
      customTypes,
    };
  } catch (err) {
    console.error('[loadWebsiteVisitsConversionConfig]', err);
    return emptyConversionConfig();
  }
}

export async function saveWebsiteVisitsConversionConfig(
  businessId: string,
  conversionConfig: WebsiteVisitsConversionConfig,
): Promise<void> {
  const validCriteria = conversionConfig.criteria
    .filter((c) => c.value)
    .map((c) => ({
      type: c.type,
      value: c.value,
      ...(c.conversionTypeId ? { conversionTypeId: c.conversionTypeId } : {}),
    }));
  const toSave: WebsiteVisitsConversionConfig = {
    enabled: conversionConfig.enabled,
    criteria: validCriteria,
    customTypes: (conversionConfig.customTypes || []).filter(
      (type) => type.id.trim() && type.label.trim(),
    ),
  };

  const { data: existing, error: readError } = await supabase
    .from('business_analytics_config')
    .select('id, config')
    .eq('business_id', businessId)
    .maybeSingle();
  if (readError) throw readError;

  const nextConfig = {
    ...((existing?.config as Record<string, unknown>) || {}),
    conversionConfig: toSave,
  };

  if (existing?.id) {
    const { error } = await supabase
      .from('business_analytics_config')
      .update({ config: nextConfig })
      .eq('id', existing.id);
    if (error) throw error;
    return;
  }

  const { error } = await supabase.from('business_analytics_config').insert({
    business_id: businessId,
    provider: 'custom',
    is_active: false,
    config: nextConfig,
  });
  if (error) throw error;
}

export async function fetchConversionRouteOptions(
  businessId: string,
  period: WebsiteVisitsPeriod,
): Promise<ConversionRouteOption[]> {
  const { startIso, endIso } = periodUtcRange(period);
  const { data, error } = await supabase.rpc('get_conversion_routes', {
    p_business_id: businessId,
    p_start_date: startIso,
    p_end_date: endIso,
  });
  if (error) throw error;
  return (data as ConversionRouteOption[]) || [];
}

export async function fetchConversionClickOptions(
  businessId: string,
  period: WebsiteVisitsPeriod,
): Promise<ConversionClickOption[]> {
  const { startIso, endIso } = periodUtcRange(period);
  const { data, error } = await supabase.rpc('get_conversion_clicks', {
    p_business_id: businessId,
    p_start_date: startIso,
    p_end_date: endIso,
  });
  if (error) throw error;
  return (data as ConversionClickOption[]) || [];
}

function previousRangeFor(range: DateRangeInput): DateRangeInput {
  const durationMs = range.end.getTime() - range.start.getTime();
  const prevEnd = new Date(range.start.getTime() - 1);
  prevEnd.setHours(23, 59, 59, 999);
  const prevStart = new Date(prevEnd.getTime() - durationMs);
  prevStart.setHours(0, 0, 0, 0);
  return { start: prevStart, end: prevEnd };
}

function resolveVisitsRange(
  periodOrRange: WebsiteVisitsPeriod | DateRangeInput,
): { range: DateRangeInput; prev: DateRangeInput } {
  const range =
    typeof periodOrRange === 'string'
      ? getOnlineBookingVolumeRange(periodOrRange)
      : periodOrRange;
  return { range, prev: previousRangeFor(range) };
}

type PageViewEventRow = {
  timestamp: string;
  session_id: string | null;
  visitor_id: string | null;
  user_agent: string | null;
  referrer: string | null;
  page_url: string | null;
};

async function fetchAllPageViewEvents(
  businessId: string,
  startIso: string,
  endIso: string,
): Promise<PageViewEventRow[]> {
  const pageSize = 1000;
  let events: PageViewEventRow[] = [];
  let page = 0;
  let hasMore = true;

  while (hasMore) {
    const { data, error } = await supabase
      .from('analytics_events')
      .select('timestamp, session_id, visitor_id, user_agent, referrer, page_url')
      .eq('business_id', businessId)
      .eq('event_type', 'page_view')
      .gte('timestamp', startIso)
      .lte('timestamp', endIso)
      .range(page * pageSize, (page + 1) * pageSize - 1)
      .order('timestamp', { ascending: true });

    if (error) throw error;
    if (data && data.length > 0) {
      events = events.concat(data);
      hasMore = data.length === pageSize;
      page += 1;
    } else {
      hasMore = false;
    }
  }

  // Same bot/dev-traffic exclusions as the SQL counting functions
  return events.filter((e) => !isExcludedAnalyticsEvent(e));
}

function localDateStr(timestamp: string): string {
  const date = new Date(timestamp);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

function groupByDate(
  events: Array<{ timestamp: string; session_id: string | null; visitor_id: string | null }>,
) {
  const sessions: Record<string, Set<string>> = {};
  const visitors: Record<string, Set<string>> = {};
  for (const event of events) {
    const d = localDateStr(event.timestamp);
    if (!sessions[d]) sessions[d] = new Set();
    if (!visitors[d]) visitors[d] = new Set();
    if (event.session_id) sessions[d].add(event.session_id);
    if (event.visitor_id) visitors[d].add(event.visitor_id);
  }
  return { sessions, visitors };
}

function toCountMap(setMap: Record<string, Set<string>>): Record<string, number> {
  const out: Record<string, number> = {};
  for (const d of Object.keys(setMap)) out[d] = setMap[d].size;
  return out;
}

function enumerateDates(start: Date, end: Date): string[] {
  const dates: string[] = [];
  const cursor = new Date(start.getFullYear(), start.getMonth(), start.getDate());
  const endLocal = new Date(end.getFullYear(), end.getMonth(), end.getDate());
  while (cursor <= endLocal) {
    dates.push(
      `${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, '0')}-${String(cursor.getDate()).padStart(2, '0')}`,
    );
    cursor.setDate(cursor.getDate() + 1);
  }
  return dates;
}

/** Align previous-period counts to current labels by day index (same-length windows). */
function mapPreviousByIndex(
  allDates: string[],
  previousPeriodDates: string[],
  prevByDate: Record<string, number>,
): { values: number[]; previousPeriodDates: string[] } {
  const values = allDates.map((_, i) => {
    const prevKey = previousPeriodDates[i];
    return prevKey ? prevByDate[prevKey] || 0 : 0;
  });
  return { values, previousPeriodDates };
}

function criteriaBuckets(criteria: ConversionCriterion[]) {
  return {
    routes: criteria.filter((c) => c.type === 'route' && c.value).map((c) => c.value),
    clicks: criteria.filter((c) => c.type === 'click' && c.value).map((c) => c.value),
    urlContains: criteria
      .filter((c) => c.type === 'url_contains' && c.value.trim())
      .map((c) => c.value.trim()),
  };
}

type ConversionEventRow = PageViewEventRow & {
  event_type: string;
  event_data: { path?: string; text?: string; element_id?: string; element_class?: string } | null;
};

/** First path segment, matching count_conversion_events route extraction. */
function routeFromPageUrl(pageUrl: string): string {
  const marker = pageUrl.indexOf('//');
  if (marker === -1) return pageUrl;
  const segments = pageUrl.slice(marker + 2).split('/');
  return `/${segments[1] || ''}`;
}

function eventIsConversion(
  event: ConversionEventRow,
  routes: string[],
  clicks: string[],
  urlContains: string[],
): boolean {
  const data = event.event_data || {};
  const path = typeof data.path === 'string' ? data.path : '';
  const pageUrl = event.page_url || '';

  if (event.event_type === 'page_view') {
    if (routes.length > 0 && (routes.includes(path) || routes.includes(routeFromPageUrl(pageUrl)))) {
      return true;
    }
    if (urlContains.length > 0) {
      const pathLower = path.toLowerCase();
      const urlLower = pageUrl.toLowerCase();
      if (
        urlContains.some((needle) => {
          const value = needle.trim().toLowerCase();
          return value.length > 0 && (pathLower.includes(value) || urlLower.includes(value));
        })
      ) {
        return true;
      }
    }
  }

  if (event.event_type === 'click' && clicks.length > 0) {
    const text = typeof data.text === 'string' ? data.text.trim() : '';
    const elementId = typeof data.element_id === 'string' ? data.element_id : '';
    const elementClass = typeof data.element_class === 'string' ? data.element_class : '';
    if (clicks.includes(text) || clicks.includes(elementId) || clicks.includes(elementClass)) {
      return true;
    }
  }

  return false;
}

async function fetchConversionCandidateEvents(
  businessId: string,
  startIso: string,
  endIso: string,
): Promise<ConversionEventRow[]> {
  const pageSize = 1000;
  let events: ConversionEventRow[] = [];
  let page = 0;
  let hasMore = true;

  while (hasMore) {
    const { data, error } = await supabase
      .from('analytics_events')
      .select('timestamp, session_id, user_agent, referrer, page_url, event_type, event_data')
      .eq('business_id', businessId)
      .in('event_type', ['page_view', 'click'])
      .gte('timestamp', startIso)
      .lte('timestamp', endIso)
      .range(page * pageSize, (page + 1) * pageSize - 1)
      .order('timestamp', { ascending: true });

    if (error) throw error;
    if (data && data.length > 0) {
      events = events.concat(data as ConversionEventRow[]);
      hasMore = data.length === pageSize;
      page += 1;
    } else {
      hasMore = false;
    }
  }

  return events.filter((event) => !isExcludedAnalyticsEvent(event));
}

/** Distinct converting sessions per day. One session counts once that day, across every label. */
function conversionsByDate(
  events: ConversionEventRow[],
  criteria: ConversionCriterion[],
): Record<string, number> {
  const { routes, clicks, urlContains } = criteriaBuckets(criteria);
  const sessions: Record<string, Set<string>> = {};
  for (const event of events) {
    if (!event.session_id || !eventIsConversion(event, routes, clicks, urlContains)) continue;
    const day = localDateStr(event.timestamp);
    if (!sessions[day]) sessions[day] = new Set();
    sessions[day].add(event.session_id);
  }
  return toCountMap(sessions);
}

function conversionDataset(dates: string[], counts: Record<string, number>): WebsiteVisitsChartDataset {
  return {
    label: 'Conversions',
    data: dates.map((date) => counts[date] || 0),
    borderColor: 'rgb(234, 179, 8)',
    backgroundColor: 'rgba(234, 179, 8, 0.18)',
    tension: 0.4,
    fill: true,
    pointRadius: 4,
    pointHoverRadius: 6,
    pointBackgroundColor: 'rgb(234, 179, 8)',
    pointBorderColor: 'white',
    pointBorderWidth: 2,
    yAxisID: 'y1',
    actualDates: dates,
  };
}

async function countMatchingConversions(
  businessId: string,
  startIso: string,
  endIso: string,
  criteria: ConversionCriterion[],
): Promise<number> {
  const { routes, clicks, urlContains } = criteriaBuckets(criteria);
  if (routes.length === 0 && clicks.length === 0 && urlContains.length === 0) return 0;
  const { data } = await supabase.rpc('count_conversion_events', {
    p_business_id: businessId,
    p_start_date: startIso,
    p_end_date: endIso,
    p_routes: routes,
    p_clicks: clicks,
    p_url_contains: urlContains,
  });
  return Number(data) || 0;
}

/** Same Website Visits Graph data as the KPI / analytics report (visits + unique + optional conversion). */
export async function fetchWebsiteVisitsGraphData(
  businessId: string,
  periodOrRange: WebsiteVisitsPeriod | DateRangeInput = 'this_month',
  conversionConfig?: WebsiteVisitsConversionConfig,
): Promise<WebsiteVisitsGraphData> {
  try {
    const { range, prev } = resolveVisitsRange(periodOrRange);
    const { start, end } = range;

    const startDateUTC = new Date(Date.UTC(start.getFullYear(), start.getMonth(), start.getDate(), 0, 0, 0));
    const endDateUTC = new Date(Date.UTC(end.getFullYear(), end.getMonth(), end.getDate(), 23, 59, 59));
    const prevStartDateUTC = new Date(
      Date.UTC(prev.start.getFullYear(), prev.start.getMonth(), prev.start.getDate(), 0, 0, 0),
    );
    const prevEndDateUTC = new Date(
      Date.UTC(prev.end.getFullYear(), prev.end.getMonth(), prev.end.getDate(), 23, 59, 59, 999),
    );

    const activeCriteria = (conversionConfig?.criteria || []).filter((criterion) => criterion.value);
    const measureConversions =
      !!conversionConfig?.enabled &&
      activeCriteria.some(
        (criterion) =>
          criterion.type === 'route' || criterion.type === 'click' || criterion.type === 'url_contains',
      );

    const [currentEvents, previousEvents, conversionEvents] = await Promise.all([
      fetchAllPageViewEvents(businessId, startDateUTC.toISOString(), endDateUTC.toISOString()),
      fetchAllPageViewEvents(businessId, prevStartDateUTC.toISOString(), prevEndDateUTC.toISOString()),
      measureConversions
        ? fetchConversionCandidateEvents(
            businessId,
            startDateUTC.toISOString(),
            endDateUTC.toISOString(),
          )
        : Promise.resolve([] as ConversionEventRow[]),
    ]);

    const currentGrouped = groupByDate(currentEvents);
    const previousGrouped = groupByDate(previousEvents);
    const currentVisitsByDate = toCountMap(currentGrouped.sessions);
    const previousVisitsByDate = toCountMap(previousGrouped.sessions);
    const currentUniqueByDate = toCountMap(currentGrouped.visitors);
    const previousUniqueByDate = toCountMap(previousGrouped.visitors);

    const allDates = enumerateDates(start, end);
    const previousPeriodDates = enumerateDates(prev.start, prev.end);
    const chartLabels = allDates.map((date) => {
      const [year, month, day] = date.split('-').map(Number);
      return new Date(year, month - 1, day).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
      });
    });

    const currentVisitsData = allDates.map((date) => currentVisitsByDate[date] || 0);
    const currentUniqueData = allDates.map((date) => currentUniqueByDate[date] || 0);
    const prevVisits = mapPreviousByIndex(allDates, previousPeriodDates, previousVisitsByDate);
    const prevUnique = mapPreviousByIndex(allDates, previousPeriodDates, previousUniqueByDate);
    const conversionSeries = measureConversions
      ? conversionDataset(allDates, conversionsByDate(conversionEvents, activeCriteria))
      : null;

    const [{ data: totalVisits }, { data: totalUnique }] = await Promise.all([
      supabase.rpc('count_website_visits', {
        p_business_id: businessId,
        p_start_date: startDateUTC.toISOString(),
        p_end_date: endDateUTC.toISOString(),
      }),
      supabase.rpc('count_unique_visitors', {
        p_business_id: businessId,
        p_start_date: startDateUTC.toISOString(),
        p_end_date: endDateUTC.toISOString(),
      }),
    ]);

    const chartData: WebsiteVisitsChartData = {
      labels: chartLabels,
      datasets: [
        {
          label: 'Current Period (Daily Visits)',
          data: currentVisitsData,
          borderColor: 'rgb(59, 130, 246)',
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          tension: 0.4,
          fill: true,
          pointRadius: 4,
          pointHoverRadius: 6,
          pointBackgroundColor: 'rgb(59, 130, 246)',
          pointBorderColor: 'white',
          pointBorderWidth: 2,
          actualDates: allDates,
        },
        {
          label: 'Previous Period (Daily Visits)',
          data: prevVisits.values,
          borderColor: 'rgba(239, 68, 68, 0.4)',
          backgroundColor: 'rgba(239, 68, 68, 0.05)',
          tension: 0.4,
          fill: false,
          pointRadius: 3,
          pointHoverRadius: 5,
          pointBackgroundColor: 'rgba(239, 68, 68, 0.4)',
          pointBorderColor: 'white',
          pointBorderWidth: 2,
          borderDash: [5, 5],
          actualDates: prevVisits.previousPeriodDates,
        },
        ...(conversionSeries ? [conversionSeries] : []),
      ],
    };

    const uniqueChartData: WebsiteVisitsChartData = {
      labels: chartLabels,
      datasets: [
        {
          label: 'Current Period (Unique Daily Visitors)',
          data: currentUniqueData,
          borderColor: 'rgb(147, 51, 234)',
          backgroundColor: 'rgba(147, 51, 234, 0.1)',
          tension: 0.4,
          fill: true,
          pointRadius: 4,
          pointHoverRadius: 6,
          pointBackgroundColor: 'rgb(147, 51, 234)',
          pointBorderColor: 'white',
          pointBorderWidth: 2,
          actualDates: allDates,
        },
        {
          label: 'Previous Period (Unique Daily Visitors)',
          data: prevUnique.values,
          borderColor: 'rgba(239, 68, 68, 0.4)',
          backgroundColor: 'rgba(239, 68, 68, 0.05)',
          tension: 0.4,
          fill: false,
          pointRadius: 3,
          pointHoverRadius: 5,
          pointBackgroundColor: 'rgba(239, 68, 68, 0.4)',
          pointBorderColor: 'white',
          pointBorderWidth: 2,
          borderDash: [5, 5],
          actualDates: prevUnique.previousPeriodDates,
        },
        ...(conversionSeries ? [conversionSeries] : []),
      ],
    };

    let conversionRate: string | undefined;
    let conversionTypes: ConversionTypeTotal[] = [];
    if (conversionConfig?.enabled && conversionConfig.criteria.length > 0) {
      const startIso = startDateUTC.toISOString();
      const endIso = endDateUTC.toISOString();
      const activeCriteria = conversionConfig.criteria.filter((c) => c.value);
      const { routes, clicks, urlContains } = criteriaBuckets(activeCriteria);

      if (routes.length > 0 || clicks.length > 0 || urlContains.length > 0) {
        const conversionCount = await countMatchingConversions(
          businessId,
          startIso,
          endIso,
          activeCriteria,
        );
        const totalVisitsNum = totalVisits || 0;
        conversionRate =
          totalVisitsNum > 0 ? ((conversionCount / totalVisitsNum) * 100).toFixed(1) : '0.0';
      }

      const catalog = conversionTypeOptions(conversionConfig.customTypes || []);
      const labeled = catalog.filter((type) =>
        activeCriteria.some((c) => c.conversionTypeId === type.id),
      );
      conversionTypes = await Promise.all(
        labeled.map(async (type) => ({
          id: type.id,
          label: type.label,
          count: await countMatchingConversions(
            businessId,
            startIso,
            endIso,
            activeCriteria.filter((c) => c.conversionTypeId === type.id),
          ),
        })),
      );
    }

    return {
      value: (totalVisits || 0).toLocaleString(),
      uniqueValue: (totalUnique || 0).toLocaleString(),
      label: 'Website Visits',
      chartData,
      uniqueChartData,
      conversionRate,
      conversionTypes,
    };
  } catch (err) {
    console.error('[fetchWebsiteVisitsGraphData]', err);
    return emptyWebsiteVisitsGraphData();
  }
}

/** Year-to-date website visits by calendar month (Jan → current). */
export async function fetchWebsiteVisitsByMonthYtd(
  businessId: string,
  conversionConfig?: WebsiteVisitsConversionConfig,
): Promise<{
  months: WebsiteVisitsMonthYtdRow[];
  yearVisits: number;
  yearUnique: number;
  yearConversionRate?: number;
  yearLabel: string;
}> {
  const now = new Date();
  const year = now.getFullYear();
  const yearStart = new Date(Date.UTC(year, 0, 1, 0, 0, 0));
  const yearEnd = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59));

  const [{ data: visitsByMonth, error: visitsError }, { data: uniqueByMonth, error: uniqueError }] =
    await Promise.all([
      supabase.rpc('get_website_visits_by_month', {
        p_business_id: businessId,
        p_start_date: yearStart.toISOString(),
        p_end_date: yearEnd.toISOString(),
      }),
      supabase.rpc('get_unique_visitors_by_month', {
        p_business_id: businessId,
        p_start_date: yearStart.toISOString(),
        p_end_date: yearEnd.toISOString(),
      }),
    ]);

  if (visitsError) throw visitsError;
  if (uniqueError) throw uniqueError;

  const visitsMap: Record<string, number> = {};
  for (const row of visitsByMonth || []) {
    visitsMap[row.month] = Number(row.visit_count) || 0;
  }
  const uniqueMap: Record<string, number> = {};
  for (const row of uniqueByMonth || []) {
    uniqueMap[row.month] = Number(row.visit_count) || 0;
  }

  const routeCriteria =
    conversionConfig?.enabled
      ? conversionConfig.criteria.filter((c) => c.type === 'route' && c.value).map((c) => c.value)
      : [];
  const clickCriteria =
    conversionConfig?.enabled
      ? conversionConfig.criteria.filter((c) => c.type === 'click' && c.value).map((c) => c.value)
      : [];
  const urlContainsCriteria =
    conversionConfig?.enabled
      ? conversionConfig.criteria
          .filter((c) => c.type === 'url_contains' && c.value.trim())
          .map((c) => c.value.trim())
      : [];
  const measureConversion =
    !!conversionConfig?.enabled &&
    (routeCriteria.length > 0 || clickCriteria.length > 0 || urlContainsCriteria.length > 0);
  const labeledTypes = conversionConfig?.enabled
    ? conversionTypeOptions(conversionConfig.customTypes || []).filter((type) =>
        conversionConfig.criteria.some((c) => c.value && c.conversionTypeId === type.id),
      )
    : [];

  const months: WebsiteVisitsMonthYtdRow[] = [];
  let yearVisits = 0;
  let yearUnique = 0;
  let yearConversionSessions = 0;
  const yearTypeCounts = new Map<string, number>();

  for (let m = 0; m <= now.getMonth(); m += 1) {
    const monthKey = `${year}-${String(m + 1).padStart(2, '0')}`;
    const visits = visitsMap[monthKey] || 0;
    const unique = uniqueMap[monthKey] || 0;
    yearVisits += visits;
    yearUnique += unique;

    let conversionRate: number | undefined;
    let conversionTypes: ConversionTypeTotal[] = [];
    if (measureConversion) {
      const range = getMonthDateRange(monthKey);
      const startUTC = new Date(
        Date.UTC(range.start.getFullYear(), range.start.getMonth(), range.start.getDate(), 0, 0, 0),
      );
      const endUTC = new Date(
        Date.UTC(range.end.getFullYear(), range.end.getMonth(), range.end.getDate(), 23, 59, 59),
      );
      const startIso = startUTC.toISOString();
      const endIso = endUTC.toISOString();
      const { data: conversionCount } = await supabase.rpc('count_conversion_events', {
        p_business_id: businessId,
        p_start_date: startIso,
        p_end_date: endIso,
        p_routes: routeCriteria,
        p_clicks: clickCriteria,
        p_url_contains: urlContainsCriteria,
      });
      const conv = Number(conversionCount) || 0;
      yearConversionSessions += conv;
      conversionRate = visits > 0 ? (conv / visits) * 100 : 0;
      conversionTypes = await Promise.all(
        labeledTypes.map(async (type) => {
          const count = await countMatchingConversions(
            businessId,
            startIso,
            endIso,
            conversionConfig!.criteria.filter((c) => c.value && c.conversionTypeId === type.id),
          );
          yearTypeCounts.set(type.id, (yearTypeCounts.get(type.id) || 0) + count);
          return { id: type.id, label: type.label, count };
        }),
      );
    }

    months.push({
      monthKey,
      label: new Date(year, m, 1).toLocaleDateString('en-US', {
        month: 'long',
        year: 'numeric',
      }),
      visits,
      unique,
      conversionRate,
      conversionTypes,
      isCurrentMonth: m === now.getMonth(),
    });
  }

  return {
    months,
    yearVisits,
    yearUnique,
    yearConversionRate:
      measureConversion && yearVisits > 0
        ? (yearConversionSessions / yearVisits) * 100
        : measureConversion
          ? 0
          : undefined,
    yearConversionTypes: labeledTypes.map((type) => ({
      id: type.id,
      label: type.label,
      count: yearTypeCounts.get(type.id) || 0,
    })),
    yearLabel: String(year),
  };
}
